import { formatCurrency } from "../config/currency";

// Extracted from CheckoutPage's original inline printReceipt so a second
// consumer (OrderPage's "Generate Bill", for a past/historical order) can
// reuse the exact same HTML template instead of duplicating it — the only
// pattern this codebase has for producing a receipt/bill is native
// window.print(), no PDF library involved.

export interface ReceiptItem {
  product_name: string;
  unit_price: number;
  quantity: number;
}

export interface ReceiptData {
  orderId: number;
  timestamp: Date;
  cashier: string;
  orderType: string;
  items: ReceiptItem[];
  subtotal: number;
  // Coupon-applied discount (existing mechanism).
  discountAmount: number;
  couponName?: string;
  // Catalog (product/category) discount — a separate, additive mechanism,
  // kept as its own line rather than merged into discountAmount so the two
  // are never conflated on the printed receipt.
  catalogDiscountAmount?: number;
  catalogDiscountName?: string;
  total: number;
  paymentMethod: string;
  amountTendered?: number;
  changeDue?: number;
  customerName?: string;
  address?: string;
  isOfflineSale?: boolean;
  offlineRef?: string;
  storeName?: string;
}

export const buildReceiptHtml = (s: ReceiptData): string => {
  const dateStr = s.timestamp.toLocaleDateString("en-PK", { year: "numeric", month: "short", day: "numeric" });
  const timeStr = s.timestamp.toLocaleTimeString("en-PK", { hour: "2-digit", minute: "2-digit" });
  const storeTitle = (s.storeName || "").trim() || "1MARKAZ";

  const itemRows = s.items.map((item) => {
    const lineTotal = (item.unit_price * item.quantity).toFixed(2);
    const name = item.product_name.length > 22 ? item.product_name.slice(0, 20) + "…" : item.product_name;
    return `
        <tr>
          <td>${name}</td>
          <td class="center">${item.quantity}</td>
          <td class="right">${item.unit_price.toFixed(2)}</td>
          <td class="right">${lineTotal}</td>
        </tr>`;
  }).join("");

  const catalogDiscountRow = (s.catalogDiscountAmount ?? 0) > 0 ? `
        <tr class="discount">
          <td colspan="3">Item Discount${s.catalogDiscountName ? ` (${s.catalogDiscountName})` : ""}</td>
          <td class="right">-${(s.catalogDiscountAmount ?? 0).toFixed(2)}</td>
        </tr>` : "";

  const couponDiscountRow = s.discountAmount > 0 ? `
        <tr class="discount">
          <td colspan="3">Discount${s.couponName ? ` (${s.couponName})` : ""}</td>
          <td class="right">-${s.discountAmount.toFixed(2)}</td>
        </tr>` : "";

  const cashRows = s.paymentMethod === "cash" ? `
        <tr><td colspan="2">Amount Tendered</td><td colspan="2" class="right">${formatCurrency(s.amountTendered ?? 0)}</td></tr>
        <tr class="change"><td colspan="2">Change Due</td><td colspan="2" class="right">${formatCurrency(s.changeDue ?? 0)}</td></tr>` : "";

  const customerRow = s.customerName ? `<tr><td>Customer</td><td>${s.customerName}</td></tr>` : "";
  const addressRow = s.address ? `<tr><td>Address</td><td>${s.address}</td></tr>` : "";

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Receipt — Order #${s.orderId}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, monospace, sans-serif;
      font-size: 12.5px;
      font-weight: 600;
      width: 300px;
      margin: 0 auto;
      padding: 12px 8px;
      color: #000;
      line-height: 1.35;
    }
    .center { text-align: center; }
    .right  { text-align: right; }
    h1 {
      font-size: 18px;
      font-weight: 800;
      text-align: center;
      letter-spacing: 1.5px;
      margin-bottom: 2px;
      text-transform: uppercase;
      color: #000;
    }
    .subtitle {
      text-align: center;
      font-size: 11px;
      font-weight: 600;
      color: #000;
      margin-bottom: 8px;
    }
    .divider {
      border: none;
      border-top: 1.5px dashed #000;
      margin: 6px 0;
    }
    .meta td {
      padding: 2px 0;
      font-size: 12px;
      font-weight: 600;
      color: #000;
    }
    .meta td:last-child { text-align: right; }
    table.items { width: 100%; border-collapse: collapse; margin: 4px 0; }
    table.items th {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      border-bottom: 1.5px solid #000;
      padding: 3px 0;
      color: #000;
    }
    table.items td {
      padding: 3px 0;
      vertical-align: top;
      font-size: 12px;
      font-weight: 600;
      color: #000;
    }
    table.items th:nth-child(2), table.items td:nth-child(2) { text-align: center; width: 28px; }
    table.items th:nth-child(3), table.items td:nth-child(3),
    table.items th:nth-child(4), table.items td:nth-child(4) { text-align: right; width: 52px; }
    .subtotal td {
      border-top: 1.5px dashed #000;
      padding-top: 4px;
      font-weight: 700;
      color: #000;
    }
    .discount td {
      color: #000;
      font-weight: 700;
    }
    .grand td {
      font-size: 15px;
      font-weight: 800;
      border-top: 1.5px solid #000;
      padding-top: 5px;
      color: #000;
    }
    .change td { font-weight: 700; }
    .footer {
      text-align: center;
      font-size: 11px;
      font-weight: 600;
      margin-top: 10px;
      line-height: 1.6;
      color: #000;
    }
    .copyright {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 1px;
      margin-top: 5px;
      text-transform: uppercase;
      color: #000;
    }
    @media print {
      body { width: 100%; }
      @page { margin: 6mm; size: 80mm auto; }
    }
  </style>
</head>
<body>
  <h1>${storeTitle}</h1>
  <p class="subtitle">Point of Sale Receipt${s.isOfflineSale ? " — OFFLINE SALE" : ""}</p>
  <hr class="divider"/>
  <table class="meta" width="100%">
    <tr><td>Order #</td><td>${s.orderId}${s.isOfflineSale ? " (Offline Ref — Pending Sync)" : ""}</td></tr>
    <tr><td>Date</td><td>${dateStr}</td></tr>
    <tr><td>Time</td><td>${timeStr}</td></tr>
    <tr><td>Cashier</td><td>${s.cashier}</td></tr>
    <tr><td>Type</td><td>${s.orderType.charAt(0).toUpperCase() + s.orderType.slice(1)}</td></tr>
    ${customerRow}
    ${addressRow}
  </table>
  <hr class="divider"/>
  <table class="items">
    <thead>
      <tr>
        <th>Item</th>
        <th>Qty</th>
        <th>Price</th>
        <th>Total</th>
      </tr>
    </thead>
    <tbody>
      ${itemRows}
      <tr class="subtotal">
        <td colspan="3">Subtotal</td>
        <td class="right">${formatCurrency(s.subtotal)}</td>
      </tr>
      ${catalogDiscountRow}
      ${couponDiscountRow}
      <tr class="grand">
        <td colspan="3">TOTAL</td>
        <td class="right">${formatCurrency(s.total)}</td>
      </tr>
    </tbody>
  </table>
  <hr class="divider"/>
  <table class="items">
    <tbody>
      <tr><td colspan="2">Payment</td><td colspan="2" class="right">${s.paymentMethod.toUpperCase()}</td></tr>
      ${cashRows}
    </tbody>
  </table>
  <hr class="divider"/>
  <div class="footer">
    <p>Thank you for shopping!</p>
    <p>Please come again.</p>
    <hr class="divider"/>
    <p class="copyright">&copy; 1Markaz</p>
  </div>
</body>
</html>`;
};

// Split out from printReceiptHtml below so receiptPrinter.ts can call this
// synchronously, before any await, and fill in the content later once it's
// ready — window.open() only reliably escapes popup blockers when it's
// still close enough to the original click; calling it for the first time
// after async work (a fetch, a WebUSB device picker the user had to
// interact with) risks the browser no longer treating it as gesture-linked
// and blocking it outright.
export const openReceiptWindow = (): Window | null =>
  window.open("", "_blank", "width=380,height=600,scrollbars=yes");

// Returns false (instead of throwing) when the popup is blocked/unavailable,
// so callers can surface their own "allow popups" message via whatever
// notification mechanism they already use (a snackbar, an alert, etc.)
// rather than this shared utility depending on either.
//
// `existingWindow`: pass a window already opened via openReceiptWindow() to
// write into it instead of opening a fresh one (the normal path, via
// receiptPrinter.ts's printReceipt). Omit only for a direct, synchronous
// call with no preceding async work of your own.
export const printReceiptHtml = (html: string, existingWindow?: Window | null): boolean => {
  const win = existingWindow !== undefined ? existingWindow : openReceiptWindow();
  if (!win || win.closed) return false;
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); }, 250);
  return true;
};
